import { PaymentLaunchContext } from '@common/entities';
import type { LeasingJourneyTheme } from '@common/ui';
import { EntrataSession } from 'auth/EntrataSession';

import { Applicant } from 'types/applicant';
import {
  readEntrataRedirectLinkFromSession,
  readStoredLaunchQueryParam,
} from 'utils/viewerLaunchContextSession';

export function capturePaymentLaunchContext(locale: string): PaymentLaunchContext {
  return {
    returnLink: readEntrataRedirectLinkFromSession() || undefined,
    websiteId: readStoredLaunchQueryParam('websiteId') || undefined,
    locale,
    brandingColors: sessionStorage.getItem('brandingColors') ?? undefined,
    brandingMedia: sessionStorage.getItem('brandingMedia') ?? undefined,
    theme: sessionStorage.getItem('leasingJourneySdkTheme') ?? undefined,
    sessionOrigin: EntrataSession.getSessionOrigin() ?? undefined,
  };
}

export function restorePaymentLaunchContext(
  context: PaymentLaunchContext,
  applicant: Applicant,
): void {
  const values: Record<string, string | undefined> = {
    entrataRedirectLink: context.returnLink,
    brandingColors: context.brandingColors,
    brandingMedia: context.brandingMedia,
    leasingJourneySdkTheme: context.theme,
    paymentHandoffThemeScope: `${applicant.org.id}/${applicant.propertyId}`,
    'viewer.sessionOrigin': context.sessionOrigin,
  };
  Object.entries(values).forEach(([key, value]) => {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  });
  sessionStorage.setItem(
    'queryParams',
    JSON.stringify({
      orgId: String(applicant.org.id),
      propertyId: String(applicant.propertyId),
      rwxDomain: applicant.org.domain,
      websiteId: context.websiteId,
      entrataRedirectLink: context.returnLink,
    }),
  );
}

export function readStoredSdkTheme(paymentRestored = false): LeasingJourneyTheme | null {
  try {
    const launchContextId = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : '',
    ).get('launchContextId');
    const storedLaunchContextId = sessionStorage.getItem('leasingJourneySdkThemeLaunchContextId');
    const routeScope = window.location.pathname.split('/').slice(1, 3).join('/');
    const restoredScope = sessionStorage.getItem('paymentHandoffThemeScope');
    if (
      !paymentRestored &&
      !(restoredScope === routeScope && !launchContextId) &&
      (!launchContextId || storedLaunchContextId !== launchContextId)
    )
      return null;

    const storedTheme = sessionStorage.getItem('leasingJourneySdkTheme');
    return storedTheme ? (JSON.parse(storedTheme) as LeasingJourneyTheme) : null;
  } catch {
    return null;
  }
}
